import dayjs from 'dayjs';
import {makeAutoObservable, reaction, runInAction, when} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {CoachHistoryExpandedCommon} from '../../api/coachHistory/types';
import type {ConferenceApiModel} from '../../api/conference';
import type {MembershipApiModel} from '../../api/membership';
import type {ApiListResponseExpanded, Expand} from '../../api/types';
import type {BNClientCoachHistoryListParams} from '../../api/types/bnrks/methods';
import type {CoreClientConferencesCreateParams} from '../../api/types/core/methods';
import type {Nullable} from '../../utils/utility-types';
import type {CalendarController} from './Calendar.controller';
import type {TimeslotsStore} from './Timeslots.store';
import type {
  CoachCoverage,
  ConferenceType,
  DataModel,
  Membership,
  Strategy,
} from './types';

import {coachHistoryExpandCommon} from '../../api/coachHistory/expand';
import {conferenceStore} from '../../api/conference';
import {courseExpandCommon} from '../../api/course';
import {apiRequest} from '../../api/utils/apiRequest';
import {expandObj} from '../../api/utils/expand';
import {membershipFetchList} from '../../services/entity-action/membership/membership.fetch-list';
import {dateTimeObjToTimestamp} from '../../utils/datetime';
import {isCourseCorporate} from '../../utils/entity/course';
import {SCHEDULE_SLOT_DURATION} from '../../utils/entity/user';
import {envManager} from '../../utils/env';
import {logger} from '../../utils/logger';
import {InitialLoadManager, ReactionDisposeManager} from '../../utils/store';
import {isDefined} from '../../utils/typecheck';
import {CoachModel} from './Coach.model';
import {CoachesStore} from './Coaches.store';
import {CourseModel} from './Course.model';
import {type TimeslotModel} from './Timeslot.model';
import {NoCourseError, NoMembershipError} from './utils/errors';

export class LiveSessionStrategy implements Strategy {
  coaches: CoachesStore = new CoachesStore();
  conferenceToRescheduleId: null | string = null;
  conferenceType: ConferenceType = 'live';
  course: CourseModel | null = null;
  readonly courseId: string;
  dataTarget: DataModel | null = null;
  disposer = new ReactionDisposeManager();
  initialLoad = new InitialLoadManager();
  readonly isShowBuyMoreButton = false;
  private calendar: CalendarController;
  private coachHistory: Nullable<CoachHistoryExpandedCommon> = null;
  private membership: Membership;
  private timeslots: TimeslotsStore;

  constructor(courseId: string) {
    this.courseId = courseId;
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
    this.initialLoad.doneWhen(() => {
      return isDefined(this.course) && this.coaches.all.isNotEmpty;
    });
  }

  async init() {
    try {
      await this.fetchMembership();

      if (envManager.isB2b) {
        await this.fetchCoachHistory();
      }
    } catch (e) {
      if (e instanceof NoMembershipError) {
        this.initialLoad.setDone();
      } else {
        logger.error(e);

        throw e;
      }
    }

    if (this.membership) {
      runInAction(() => {
        this.course = new CourseModel(this.membership.course);
        this.dataTarget = this.course;

        if (isCourseCorporate(this.course.entity)) {
          // corporate clients should be able book conference for the next day only
          this.timeslots.setIsTodayAvailableForBooking(false);
        }
      });

      if (this.course) {
        this.calendar.init(this.course.bookingRange);
      }

      this.coaches.init(this.membership.course?.program.expanded_coaches || []);
    }
  }

  destruct() {
    this.disposer.dispose();
  }

  get coachCoverage(): Nullable<CoachCoverage> {
    if (!this.coachHistory) {
      return null;
    }

    return {
      newCoach: new CoachModel(this.coachHistory.new_coach),
      oldCoach: new CoachModel(this.coachHistory.old_coach),
      period: {
        end: dateTimeObjToTimestamp(this.coachHistory.end_date),
        start: dateTimeObjToTimestamp(this.coachHistory.start_date),
      },
    };
  }

  get conferenceDuration() {
    return this.course?.conferenceDuration || SCHEDULE_SLOT_DURATION;
  }

  get hasCoachCoverage() {
    return Boolean(this.coachHistory?.new_coach_id);
  }

  get isShowCoachSelect() {
    return this.coaches.all.length > 1;
  }

  get isTimeslotsLoaded() {
    return Boolean(this.timeslots?.initialLoad.isDone);
  }

  get shouldGuessTimezone() {
    return false;
  }

  get shouldShowAvailableConferences() {
    if (!this.course) {
      return false;
    }

    if (isCourseCorporate(this.course.entity)) {
      const perPeriodConferences = this.course.conferenceLimits.perPeriod ?? 0;

      return perPeriodConferences > 0;
    }

    return true;
  }

  get shouldShowBookButton() {
    return (
      this.initialLoad.isDone && Boolean(this.course?.shouldShowBookButton)
    );
  }

  get shouldShowTimezone() {
    return true;
  }

  get title() {
    const i18nKey = this.conferenceToRescheduleId
      ? 'reschedule_session'
      : 'schedule_session';
    const i18nTypeKey = this.conferenceType === 'chat' ? 'chat' : 'live';

    /**
     * @i18n_parser
     * t('shared:schedule_session.live')
     * t('shared:schedule_session.chat')
     * t('shared:reschedule_session.live')
     * t('shared:reschedule_session.chat')
     */
    return i18n.t(`shared:${i18nKey}.${i18nTypeKey}`);
  }

  async deleteConferenceToReschedule() {
    if (this.conferenceToRescheduleId) {
      try {
        await conferenceStore.client.delete(
          {
            _id: this.conferenceToRescheduleId,
          } as ConferenceApiModel,
          {
            reschedule: true,
          },
        );
      } catch (e) {
        logger.error(e);
      }
    }
  }

  inject(calendar: CalendarController, timeslots: TimeslotsStore) {
    this.calendar = calendar;
    this.timeslots = timeslots;

    return this;
  }

  isCoverageCoachAvailableForSlot(forSlot: TimeslotModel): boolean {
    if (!this.coachHistory) {
      return true;
    }

    const isNewCoach = forSlot.coachId === this.coachHistory.new_coach._id;

    if (!isNewCoach) {
      return true;
    }

    return dayjs(dateTimeObjToTimestamp(this.coachHistory.start_date)).isBefore(
      Date.now(),
    );
  }

  makeRequestParams(forSlot: TimeslotModel) {
    if (this.course) {
      const {end_date, start_date} = forSlot.makeRequestParams(
        this.course.conferenceDuration,
      );

      return {
        coach_ids: forSlot.coachId
          ? [forSlot.coachId]
          : this.coaches.selectedIds.entries,
        course_id: this.course.id,
        end_date,
        reschedule: this.conferenceToRescheduleId ? true : undefined,
        start_date,
        type:
          this.conferenceType === 'chat'
            ? ('chat' as CoreClientConferencesCreateParams['type'])
            : null,
      };
    } else {
      throw new NoCourseError();
    }
  }

  async refresh() {
    await this.course?.refresh();
  }

  refreshSlots({isSilent}: {isSilent?: boolean} = {}) {
    return this.timeslots.makeSlots({
      coaches: this.coaches.selected,
      duration: this.conferenceDuration,
      isSilent,
      newCoachHistory: this.coachHistory ?? undefined,
    });
  }

  setConferenceToRescheduleId(id: null | string) {
    this.conferenceToRescheduleId = id;
  }

  setConferenceType(type: ConferenceType) {
    this.conferenceType = type;
  }

  setupReactions(shouldFetchTimeslots: boolean) {
    this.disposer.watch(
      'change_selected',
      reaction(
        () => this.coaches.selectedIds.entries.slice(),
        (current, prev) => {
          const shouldMakeSlots =
            shouldFetchTimeslots &&
            this.course?.shouldShowBookButton &&
            this.initialLoad.isDone &&
            current[0] !== prev[0];

          if (shouldMakeSlots) {
            this.refreshSlots();
          }
        },
      ),
    );

    this.disposer.watch(
      'change_date_range',
      reaction(
        () => this.calendar.range,
        () => {
          const shouldMakeSlots =
            shouldFetchTimeslots &&
            this.course?.shouldShowBookButton &&
            this.initialLoad.isDone;

          if (shouldMakeSlots) {
            this.refreshSlots();
          }
        },
      ),
    );

    this.disposer.watch(
      'set_data_target',
      when(
        () => isDefined(this.course),
        () => (this.dataTarget = this.course as CourseModel),
      ),
    );

    this.disposer.watch(
      'cancel_conference',
      reaction(
        () => conferenceStore.client.deleting,
        deleting => {
          if (deleting.success) {
            this.refresh();
          }
        },
      ),
    );

    return this;
  }

  private async fetchCoachHistory() {
    const {
      _expanded,
      _items,
    }: ApiListResponseExpanded<CoachHistoryExpandedCommon> =
      await apiRequest<BNClientCoachHistoryListParams>({
        method: 'client.coach_history.list',
        params: {
          expand: coachHistoryExpandCommon,
          limit: 1,
          query: [
            ['type', '==', 'coach_coverage'],
            ['status', 'in', ['ongoing', 'assigned']],
            ['course_id', '==', this.courseId],
          ],
          sort: [['created', -1]],
        },
      });

    runInAction(() => {
      if (_items.length) {
        this.coachHistory = expandObj(_items[0]!, {
          expand: coachHistoryExpandCommon,
          expanded: _expanded,
        });
      }
    });
  }

  private async fetchMembership() {
    const expand: Expand = {
      membership: [['course_id', null, courseExpandCommon]],
    };

    const memberships = (await membershipFetchList.asClient({
      params: {
        expand,
        query: [
          ['course_id', '==', this.courseId],
          [
            'status',
            '!in',
            [
              'proposed',
              'requested',
              envManager.isCore ? 'declined' : null,
            ].filter(Boolean) as MembershipApiModel['status'][],
          ],
        ],
      },
    })) as Membership[];

    const [targetMembership] = memberships;

    if (!targetMembership) {
      throw new NoMembershipError();
    }

    /**
     * As we get the course from the membership expand,
     * we need to make sure that the course is not null
     */
    if (!targetMembership.course) {
      throw new NoCourseError();
    }

    runInAction(() => {
      this.membership = targetMembership;
    });
  }
}
